import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FileText, Image as ImageIcon, Loader2, Upload, Video, X } from "lucide-react";
import { Button } from "../../../../components/tailwind/button";
import { MEDIA_CONFIGS } from "../../../../utils/constants/mediaConstants";
import { showAlert } from "../../../../store/alertSlice";
import { uploadWorkflowMediaRequest } from "../../../../store/slices/workflow";
import { resolvePublicMediaUrl } from "../../../../utils/s3MediaUrl";
import { workflowTheme } from "../../utils/workflowBuilderTheme";

const MEDIA_TYPE_TO_CONFIG = {
  image: MEDIA_CONFIGS.IMAGE,
  video: MEDIA_CONFIGS.VIDEO,
  document: MEDIA_CONFIGS.DOCUMENT
};

const MEDIA_ICONS = {
  image: ImageIcon,
  video: Video,
  document: FileText
};

function isBrowserDisplayableUrl(url) {
  if (typeof url !== "string" || !url) return false;
  return url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:");
}

function resolveDisplayableUrl(url) {
  if (isBrowserDisplayableUrl(url)) return url;
  if (typeof url === "string" && url.startsWith("s3://")) {
    return resolvePublicMediaUrl({ s3Key: url }) || null;
  }
  return null;
}

function revokeBlobUrl(url) {
  if (typeof url === "string" && url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
}

export function WorkflowMediaField({
  value,
  filename: savedFilename = "",
  pendingFileProp = null,
  mediaType = "image",
  nodeId,
  onChange,
  onPendingChange,
  saveSignal = 0,
  uploadSilent = true,
  onRegisterPendingUploadNode,
  disabled = false
}) {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [attachedFilename, setAttachedFilename] = useState(savedFilename || "");
  const [pendingFile, setPendingFile] = useState(pendingFileProp);
  const [previewUrl, setPreviewUrl] = useState(null);

  const { mediaUpload } = useSelector((state) => state.workflowSave || {});
  const { loading, error } = mediaUpload || {};

  const config = MEDIA_TYPE_TO_CONFIG[mediaType] || MEDIA_TYPE_TO_CONFIG.image;
  const Icon = MEDIA_ICONS[mediaType] || ImageIcon;
  const isUploading = loading && mediaUpload?.nodeId === nodeId;

  const setImagePreviewFromFile = useCallback((file) => {
    if (mediaType !== "image" || !file?.type?.startsWith("image/")) {
      setPreviewUrl(null);
      return;
    }
    setPreviewUrl((current) => {
      revokeBlobUrl(current);
      return URL.createObjectURL(file);
    });
  }, [mediaType]);

  useEffect(() => () => revokeBlobUrl(previewUrl), [previewUrl]);

  const imagePreviewSrc = useMemo(() => {
    if (mediaType !== "image") return null;
    if (previewUrl) return previewUrl;
    return resolveDisplayableUrl(value);
  }, [mediaType, previewUrl, value]);

  useEffect(() => {
    setAttachedFilename(savedFilename || "");
  }, [savedFilename, value]);

  useEffect(() => {
    setPendingFile(pendingFileProp);
    setImagePreviewFromFile(pendingFileProp);
  }, [pendingFileProp, setImagePreviewFromFile]);

  useEffect(() => {
    onPendingChange?.(Boolean(pendingFile));
  }, [pendingFile, onPendingChange]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file || disabled) return;

    const acceptedTypes = config.acceptedFormats;
    if (!acceptedTypes.includes(file.type)) {
      dispatch(showAlert({ message: `Invalid file type. Expected ${config.label}`, type: "error" }));
      return;
    }

    if (file.size > config.maxFileSize) {
      dispatch(showAlert({ message: `File size exceeds ${config.maxSizeText} limit`, type: "error" }));
      return;
    }

    setPendingFile(file);
    setAttachedFilename(file.name);
    setImagePreviewFromFile(file);
    onChange("", { filename: file.name, pendingFile: file });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemove = () => {
    setAttachedFilename("");
    setPendingFile(null);
    setPreviewUrl((current) => {
      revokeBlobUrl(current);
      return null;
    });
    onChange("", { filename: "", clearMediaId: true, clearPendingFile: true });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const hasUploadedMedia = Boolean(value);
  const hasPendingUpload = Boolean(pendingFile);
  const hasSelection = hasUploadedMedia || hasPendingUpload || isUploading || Boolean(previewUrl);

  const displayName = attachedFilename
    || (hasUploadedMedia ? String(value).split("/").pop() || "Uploaded file" : "");

  return (
    <div className={`rounded-lg border ${workflowTheme.border} ${workflowTheme.surface}`}>
      <div className={`px-3 py-2.5 border-b ${workflowTheme.borderSubtle} flex items-center justify-between gap-2`}>
        <div className="flex items-center gap-2 min-w-0">
          <div className={`h-8 w-8 rounded-lg ${workflowTheme.input} flex items-center justify-center shrink-0`}>
            <Icon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </div>
          <div className="min-w-0">
            <p className={`text-sm font-medium ${workflowTheme.textPrimary}`}>Upload {mediaType}</p>
            <p className={`text-xs ${workflowTheme.textSecondary}`}>{config.label} · {config.maxSizeText}</p>
          </div>
        </div>
      </div>

      <div className="px-3 py-3 space-y-2">
        {error && mediaUpload?.nodeId === nodeId && (
          <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
        )}

        {(hasSelection || isUploading) ? (
          imagePreviewSrc ? (
            <div className={`relative rounded-lg border ${workflowTheme.border} overflow-hidden ${workflowTheme.input}`}>
              <img
                src={imagePreviewSrc}
                alt={displayName || "Selected image preview"}
                className="w-full max-h-36 object-contain bg-gray-100 dark:bg-slate-800"
              />
              {isUploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <Loader2 className="w-6 h-6 animate-spin text-white" />
                </div>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute top-1.5 right-1.5 h-7 w-7 bg-white/90 dark:bg-slate-900/90 text-gray-600 hover:text-red-600 dark:hover:text-red-400 shadow-sm"
                onClick={handleRemove}
                disabled={disabled || isUploading}
                aria-label="Remove media"
              >
                <X className="w-4 h-4" />
              </Button>
              <div className={`px-2.5 py-2 border-t ${workflowTheme.borderSubtle}`}>
                <p className={`text-xs ${workflowTheme.textPrimary} truncate`}>{displayName}</p>
                {hasPendingUpload && !hasUploadedMedia && !isUploading && (
                  <p className={`text-xs ${workflowTheme.textSecondary} mt-0.5`}>
                    Uploads when you save the workflow
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className={`rounded-lg border ${workflowTheme.border} ${workflowTheme.input} px-3 py-2.5 flex items-center justify-between gap-2`}>
              <div className="min-w-0">
                <p className={`text-sm ${workflowTheme.textPrimary} truncate`}>
                  {isUploading ? "Uploading: " : hasPendingUpload && !hasUploadedMedia ? "Selected: " : "✓ "}
                  {displayName}
                </p>
                {hasPendingUpload && !hasUploadedMedia && !isUploading && (
                  <p className={`text-xs ${workflowTheme.textSecondary} mt-0.5`}>
                    Uploads when you save the workflow
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                onClick={handleRemove}
                disabled={disabled || isUploading}
                aria-label="Remove media"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )
        ) : (
          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              accept={config.extensions}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
              onChange={handleFileChange}
              disabled={disabled || isUploading}
            />
            <Button
              type="button"
              variant="outline"
              className={`w-full border-dashed ${workflowTheme.input} hover:bg-gray-100 dark:hover:bg-slate-800`}
              disabled={disabled || isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Choose file
                </>
              )}
            </Button>
          </div>
        )}

        {hasSelection && !isUploading && (
          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              accept={config.extensions}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
              onChange={handleFileChange}
              disabled={disabled}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={`w-full text-xs ${workflowTheme.input}`}
              disabled={disabled}
            >
              Replace file
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
